import { useState } from "react";
import { Send, Check, Undo2, Save, LockOpen } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { StatusChip, btnP, btnS } from "./BpKit";

const NoteDialog = ({ open, title, cta, needNote, onClose, onOk, busy }) => {
  const [note, setNote] = useState("");
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="bp-note-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <textarea data-testid="bp-founder-notes-textarea" rows={4} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
          placeholder={needNote ? "Say what needs changing (required)" : "Add a note (optional)"} className="w-full border border-slate-300 rounded-sm p-2 text-sm focus:outline-none focus:border-primary" />
        <DialogFooter>
          <button type="button" className={btnS} onClick={onClose} data-testid="bp-note-cancel">Cancel</button>
          <button type="button" className={btnP} disabled={busy || (needNote && !note.trim())} onClick={() => onOk(note)} data-testid="bp-note-confirm">{cta}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const NoteBanner = ({ plan }) => (plan.status === "returned" && plan.note ? (
  <div className="mt-3 text-sm border border-orange-300 bg-orange-50 text-orange-800 px-3 py-2 rounded-sm" data-testid="bp-sent-back-note">
    Sent back by {plan.decided_by}: “{plan.note}”
  </div>
) : null);

const dialogs = (submitLabel) => ({
  submit: { title: submitLabel, cta: "Submit", path: "submit" },
  approve: { title: "Approve this plan", cta: "Approve", path: "decide", body: { approve: true } },
  back: { title: "Send back for changes", cta: "Send back", path: "decide", body: { approve: false }, needNote: true },
  reopen: { title: "Reopen for changes", cta: "Reopen", path: "reopen" },
});

const Actions = ({ s, can, editable, dirty, busy, onSave, open, prefix, submitLabel }) => (
  <div className="flex flex-wrap gap-2 ml-auto">
    {editable && <button type="button" data-testid={`${prefix}-save-draft-button`} className={btnS} disabled={busy || !dirty} onClick={onSave}><Save className="h-4 w-4 inline mr-1" />Save draft</button>}
    {editable && s !== "none" && <button type="button" data-testid={`${prefix}-submit-button`} className={btnP} disabled={busy || dirty} onClick={() => open("submit")}><Send className="h-4 w-4 inline mr-1" />{submitLabel}</button>}
    {can.approve && s === "submitted" && (<>
      <button type="button" data-testid={`${prefix}-send-back-button`} className={btnS} disabled={busy} onClick={() => open("back")}><Undo2 className="h-4 w-4 inline mr-1" />Send back</button>
      <button type="button" data-testid={`${prefix}-approve-button`} className="bg-emerald-600 text-white h-9 px-4 text-sm rounded-sm font-medium hover:bg-emerald-700 disabled:opacity-50" disabled={busy} onClick={() => open("approve")}><Check className="h-4 w-4 inline mr-1" />Approve plan</button>
    </>)}
    {can.approve && ["approved", "submitted"].includes(s) && <button type="button" data-testid={`${prefix}-reopen-button`} className={btnS} disabled={busy} onClick={() => open("reopen")}><LockOpen className="h-4 w-4 inline mr-1" />Reopen</button>}
  </div>
);

const Hints = ({ s, editable, dirty, canApprove, prefix }) => (<>
  {editable && s !== "none" && dirty && <p className="text-xs text-slate-500 mt-2">Save your changes before submitting.</p>}
  {s === "submitted" && !canApprove && <p className="text-xs text-sky-700 mt-2" data-testid={`${prefix}-waiting`}>Waiting for approval. It's locked until the approver decides.</p>}
</>);

export const ApprovalBar = ({ plan, can, dirty, busy, onSave, onAct, prefix, submitLabel }) => {
  const [dlg, setDlg] = useState(null);
  const s = plan.status;
  const editable = can.edit && ["none", "draft", "returned"].includes(s);
  const d = dlg && dialogs(submitLabel)[dlg];
  return (
    <div className="bg-white border border-slate-300 rounded-sm p-4" data-testid={`${prefix}-approval-bar`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Status</span>
        <StatusChip s={s} tid={`${prefix}-status-chip`} />
        {plan.updated_by && <span className="text-xs text-slate-500">Last saved by {plan.updated_by}</span>}
        {dirty && <span className="text-xs text-amber-700 font-medium" data-testid={`${prefix}-unsaved`}>Unsaved changes</span>}
        <Actions s={s} can={can} editable={editable} dirty={dirty} busy={busy} onSave={onSave} open={setDlg} prefix={prefix} submitLabel={submitLabel} />
      </div>
      <Hints s={s} editable={editable} dirty={dirty} canApprove={can.approve} prefix={prefix} />
      <NoteBanner plan={plan} />
      {d && <NoteDialog key={dlg} open title={d.title} cta={d.cta} needNote={d.needNote} busy={busy} onClose={() => setDlg(null)}
        onOk={(note) => onAct(d.path, { ...(d.body || {}), note }).then((ok) => ok && setDlg(null))} />}
    </div>
  );
};
